/**
 * App notifications on this device: whether they can work here, turning
 * them on and off, and keeping them on.
 *
 * A notification is a weather warning's headline, sent by the backend when
 * the warning email goes (services/alerts/pushService) and shown by the
 * service worker (public/push-sw.js, imported into the worker vite-plugin-pwa
 * builds). Tapping it opens Today.
 *
 * Where it works: a browser with a service worker, the Push API and
 * notifications. On an iPhone or iPad only once Grow is on the home screen
 * and opened from there (Safari offers web push to home-screen apps alone),
 * so there the profile explains how instead of offering the switch. Not in
 * development, which has no service worker.
 *
 * Turning on asks the browser's permission, and only then, from the tap
 * itself (askPermission, called in the click handler before anything is
 * awaited: Safari refuses a request made any other way). Turning off tells
 * the server first, then drops the subscription.
 *
 * Kept on: the update check (shared/lib/pwaUpdate) unregisters the old
 * service worker when a new build is out, which drops its subscription.
 * renewPushSubscription, called once a worker is registered, subscribes
 * again without asking (permission is already given) and tells the server,
 * when this device had them on. A renewal that fails is reported to Sentry
 * and the profile then shows the switch off.
 *
 * Stored on the device (localStorage, each read and write guarded): the
 * endpoint last given to the server, which is also "notifications are on
 * here" (PUSH_ON_KEY), and the server key they were turned on with
 * (PUSH_KEY_KEY).
 *
 * Who uses it: user/components/WarningsCard (pushSupport, deviceSubscribed,
 * askPermission, turnOnPush, turnOffPush, notificationPermission) and
 * shared/lib/pwaUpdate (renewPushSubscription).
 *
 * @module pushNotifications
 */

import * as Sentry from '@sentry/react';
import { auth } from '@/config/firebase';
import { deletePushSubscription, savePushSubscription, type DeviceSubscription } from '@/shared/api/push';

/** Where the endpoint last given to the server is kept: present means "notifications are on here". */
const PUSH_ON_KEY = 'grow.push.endpoint';
/** Where the server key they were turned on with is kept. */
const PUSH_KEY_KEY = 'grow.push.key';
/** How long to wait for the service worker before deciding there is none (development, a blocked worker). */
const WORKER_WAIT_MS = 4000;

/** Whether notifications can work here, and if not, why. */
export type PushSupport = 'ok' | 'ios-home-screen' | 'unsupported';

/** What turning notifications on came to. */
export type TurnOnResult = 'on' | 'denied';

/**
 * A localStorage read that never throws.
 *
 * @param key - The item's name.
 * @returns Its value, or null.
 */
function readStored(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * A localStorage write (or removal, with null) that never throws.
 *
 * @param key - The item's name.
 * @param value - Its value, or null to remove it.
 */
function writeStored(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch { /* storage refused: the switch then reads off, and can be turned on again */ }
}

/**
 * Whether this is an iPhone, iPad or iPod (an iPad says it is a Mac, but has a touch screen).
 *
 * @returns True on iOS and iPadOS.
 */
export function isAppleMobile(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && (navigator.maxTouchPoints ?? 0) > 1);
}

/**
 * Whether Grow was opened from the home screen, in its own window.
 *
 * @returns True when running as an installed app.
 */
export function isHomeScreenApp(): boolean {
  if (typeof window === 'undefined') return false;
  const standalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return standalone || window.matchMedia?.('(display-mode: standalone)').matches === true;
}

/**
 * Whether this browser has what notifications need, before asking the worker.
 *
 * @returns True with a service worker, the Push API and notifications.
 */
function hasPushApis(): boolean {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/**
 * The active service worker's registration, or null when there is none in
 * time (development has none).
 *
 * @returns The registration, or null.
 */
async function workerRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!hasPushApis()) return null;
  let timer: number | undefined;
  const late = new Promise<null>((resolve) => { timer = window.setTimeout(() => resolve(null), WORKER_WAIT_MS); });
  try {
    return await Promise.race([navigator.serviceWorker.ready, late]);
  } finally {
    window.clearTimeout(timer);
  }
}

/**
 * Whether notifications can work on this device, and if not, why: an iPhone
 * or iPad not opened from the home screen, or a browser (or a development
 * build) without what they need.
 *
 * @returns 'ok', 'ios-home-screen' or 'unsupported'.
 */
export async function pushSupport(): Promise<PushSupport> {
  if (isAppleMobile() && !isHomeScreenApp()) return 'ios-home-screen';
  if (!hasPushApis()) return 'unsupported';
  return (await workerRegistration()) ? 'ok' : 'unsupported';
}

/**
 * The browser's permission for notifications from Grow.
 *
 * @returns 'granted', 'denied' or 'default' (not asked yet).
 */
export function notificationPermission(): NotificationPermission {
  return hasPushApis() ? Notification.permission : 'denied';
}

/**
 * A base64url key as the bytes PushManager.subscribe wants.
 *
 * @param key - base64url, as the server gives it.
 * @returns The bytes.
 */
function keyBytes(key: string): Uint8Array {
  const base64 = (key + '='.repeat((4 - (key.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

/**
 * A subscription as the server keeps it.
 *
 * @param subscription - The browser's.
 * @returns Its endpoint and keys.
 * @throws When the browser gave no keys (it never should).
 */
function asDevice(subscription: PushSubscription): DeviceSubscription {
  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) throw new Error('The push subscription has no keys');
  return { endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth } };
}

/**
 * This device's subscription, made with the given server key: the one there
 * is, or a new one (a subscription made with another key is dropped first).
 *
 * @param registration - The service worker's.
 * @param publicKey - The server's, base64url.
 * @returns The subscription.
 * @throws When the browser or its push service refuses.
 */
async function subscribe(registration: ServiceWorkerRegistration, publicKey: string): Promise<PushSubscription> {
  const existing = await registration.pushManager.getSubscription();
  if (existing && readStored(PUSH_KEY_KEY) === publicKey) return existing;
  if (existing) await existing.unsubscribe().catch(() => false);
  return registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) });
}

/**
 * Whether notifications are on on this device: permission is given, and the
 * browser's subscription is the one last given to the server.
 *
 * @returns True when they are on.
 */
export async function deviceSubscribed(): Promise<boolean> {
  const saved = readStored(PUSH_ON_KEY);
  if (notificationPermission() !== 'granted' || !saved) return false;
  const registration = await workerRegistration();
  const subscription = registration ? await registration.pushManager.getSubscription() : null;
  return subscription?.endpoint === saved;
}

/**
 * Asks the browser's permission for notifications, or answers at once when
 * it was given before. Call it in the tap's own handler, before anything is
 * awaited: Safari asks only inside the gesture.
 *
 * @returns The permission, once the gardener has answered.
 */
export function askPermission(): Promise<NotificationPermission> {
  if (!hasPushApis()) return Promise.resolve('denied');
  if (Notification.permission === 'granted') return Promise.resolve('granted');
  return Notification.requestPermission();
}

/**
 * Turns notifications on here, once permission is asked (askPermission):
 * subscribes and tells the server. A subscription the server could not keep
 * is dropped again, so the switch never says on when nothing will come.
 *
 * @param publicKey - The server's (getPushPublicKey).
 * @param asked - askPermission's answer, asked from the tap.
 * @returns 'on', or 'denied' when the gardener (or the browser) said no.
 * @throws A failed request (an Axios error) or the browser's refusal to subscribe.
 */
export async function turnOnPush(publicKey: string, asked: Promise<NotificationPermission>): Promise<TurnOnResult> {
  if ((await asked) !== 'granted') return 'denied';
  const registration = await workerRegistration();
  if (!registration) throw new Error('No service worker to subscribe with');
  const subscription = await subscribe(registration, publicKey);
  try {
    await savePushSubscription(asDevice(subscription));
  } catch (error) {
    await subscription.unsubscribe().catch(() => false);
    writeStored(PUSH_ON_KEY, null);
    throw error;
  }
  writeStored(PUSH_KEY_KEY, publicKey);
  writeStored(PUSH_ON_KEY, subscription.endpoint);
  return 'on';
}

/**
 * Turns notifications off here: the server forgets this device, then the
 * browser's subscription is dropped.
 *
 * @throws A failed request (an Axios error); the subscription is then kept, so the switch stays on.
 */
export async function turnOffPush(): Promise<void> {
  const registration = await workerRegistration();
  const subscription = registration ? await registration.pushManager.getSubscription() : null;
  if (subscription) await deletePushSubscription(subscription.endpoint);
  writeStored(PUSH_ON_KEY, null);
  if (subscription) await subscription.unsubscribe().catch(() => false);
}

/**
 * Keeps notifications on after the service worker has been replaced (the
 * update check unregisters it, which drops its subscription): when this
 * device turned them on and permission is still given, subscribes again
 * with the same server key when there is no subscription, and gives the
 * server any subscription it has not been given, once someone is signed in
 * (signed out, it is tried again next start). Quiet when there is nothing to
 * do; a failure is reported to Sentry, never shown (the profile's switch
 * then reads off).
 *
 * @param registration - The newly registered service worker's.
 */
export async function renewPushSubscription(registration: ServiceWorkerRegistration): Promise<void> {
  const publicKey = readStored(PUSH_KEY_KEY);
  const saved = readStored(PUSH_ON_KEY);
  if (!saved || !publicKey || notificationPermission() !== 'granted') return;
  try {
    let subscription = await registration.pushManager.getSubscription();
    if (subscription?.endpoint === saved) return;
    if (!subscription) {
      const ready = await navigator.serviceWorker.ready;
      subscription = await ready.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) });
    }
    // The token the request carries needs the signed-in person, restored from storage at start.
    await auth.authStateReady();
    if (!auth.currentUser) return;
    await savePushSubscription(asDevice(subscription));
    writeStored(PUSH_ON_KEY, subscription.endpoint);
  } catch (error) {
    writeStored(PUSH_ON_KEY, null);
    Sentry.withScope((scope) => {
      scope.setTag('where', 'renewPushSubscription');
      Sentry.captureException(error instanceof Error ? error : new Error('Could not renew notifications'));
    });
  }
}
